/**
 * Profile segments as Studio authors them: a named AND/OR tree of conditions over user profiles, served by
 * server_pray's segmentation module at `/api/studio/v1/segments`. Not the legacy lead segments (`leadSegments`).
 *
 * Values the client does not know decode to `'unknown'` instead of failing the response, so a newer server never
 * breaks an older Studio; the builder shows such a segment, field or node read-only.
 */

export const SEGMENT_STATUSES = ['draft', 'active', 'archived'] as const;
export type SegmentStatus = (typeof SEGMENT_STATUSES)[number];

export const SEGMENT_SOURCES = ['predicate', 'materialized_list'] as const;
export type SegmentSource = (typeof SEGMENT_SOURCES)[number];

export const SEGMENT_DATA_TYPES = ['boolean', 'timestamp', 'number', 'string', 'id_set'] as const;
export type SegmentDataType = (typeof SEGMENT_DATA_TYPES)[number];

export const SEGMENT_COMBINATORS = ['AND', 'OR'] as const;
export type SegmentCombinator = (typeof SEGMENT_COMBINATORS)[number];

export const RELATIVE_TIME_UNITS = ['minute', 'hour', 'day', 'week', 'month', 'year'] as const;
export type RelativeTimeUnit = (typeof RELATIVE_TIME_UNITS)[number];

/** What value an operator takes, which decides the input the builder renders for it. */
export type OperatorValueShape = 'none' | 'relative_time' | 'scalar' | 'range' | 'list' | 'event_count' | 'version';

/** The server's operator registry, by value shape. An operator missing here is one this Studio cannot edit. */
export const SEGMENT_OPERATOR_VALUE_SHAPES: Readonly<Record<string, OperatorValueShape>> = {
  is_true: 'none',
  is_false: 'none',
  is_true_or_null: 'none',
  is_false_or_null: 'none',
  more_than_ago: 'relative_time',
  more_than_ago_or_null: 'relative_time',
  within_last: 'relative_time',
  before: 'scalar',
  after: 'scalar',
  eq: 'scalar',
  neq: 'scalar',
  gt: 'scalar',
  gte: 'scalar',
  lt: 'scalar',
  lte: 'scalar',
  between: 'range',
  in: 'list',
  not_in: 'list',
  matches_regex: 'scalar',
  exists: 'none',
  not_exists: 'none',
  contains: 'scalar',
  contains_any: 'list',
  done_at_least: 'event_count',
  done_at_most: 'event_count',
  done_exactly: 'event_count',
  version_lt: 'version',
  version_gte: 'version',
};

/**
 * The filter categories the catalog files a field under, which are Braze's, in the order Braze lists them. A category
 * this Studio does not know reads as `other`, so a newer server's field still shows.
 */
export const SEGMENT_FIELD_CATEGORIES = [
  'segment_or_csv_membership',
  'custom_attribute',
  'custom_events',
  'sessions',
  'retargeting',
  'channel_subscription',
  'purchase',
  'demographic',
  'app',
  'uninstall',
  'devices',
  'location',
  'other',
] as const;
export type SegmentFieldCategory = (typeof SEGMENT_FIELD_CATEGORIES)[number];

/** Where a field's values come from: a profile column, a generated attribute, or one aspect of an event. */
export const SEGMENT_FIELD_KINDS = ['column', 'attribute', 'event'] as const;
export type SegmentFieldKind = (typeof SEGMENT_FIELD_KINDS)[number];

/** Which aspect of an event a field reads: how many times it was done, or when it was first or last done. */
export const SEGMENT_EVENT_ASPECTS = ['count', 'first_done', 'last_done'] as const;
export type SegmentEventAspect = (typeof SEGMENT_EVENT_ASPECTS)[number];

/** The event an event field reads. */
export type SegmentFieldEvent = {
  eventName: string;
  /** `null` for the event as sent with no entity type. */
  entityType: string | null;
  aspect: SegmentEventAspect | 'unknown';
};

/** A field a condition may filter on, from the catalog. */
export type SegmentField = {
  fieldKey: string;
  displayName: string;
  dataType: SegmentDataType | 'unknown';
  /** The operators the server allows on this field, in its order. */
  allowedOperators: string[];
  /**
   * When the field was retired, in ISO 8601 UTC; `null` while it is offered. A retired field is offered for no new
   * condition, but the segments that already use it keep it and keep working.
   */
  retiredAt?: string | null;
  /** Why it was retired, when the catalog says. */
  retiredReason?: string | null;
  /**
   * The category the picker files the field under. The decoder sets this and every presentation key after it: a server
   * from before field presentation sends none of them, which reads as `other`, with no family and no kind.
   */
  category?: SegmentFieldCategory;
  /** The family the field is one member of, such as `subscription_group`: the picker offers a family as one entry. */
  family?: string | null;
  /** The field's name inside its family, such as a subscription group's name. */
  memberLabel?: string | null;
  /**
   * Where the values come from. `null` when the server does not say (one from before field presentation): what a
   * condition on the field includes then depends on what it reads, which this Studio cannot know.
   */
  kind?: SegmentFieldKind | null;
  /** A profile holds several answers at once, so its list operators read "includes". */
  isArray?: boolean;
  /** Set on an event field. */
  event?: SegmentFieldEvent | null;
  /** A sentence that says what the field is, which the picker's search reads too. */
  helpText?: string | null;
};

export type RelativeTime = { n: number; unit: RelativeTimeUnit };
export type NumberRange = { lo: number; hi: number };
/** How many times an event was done: within the last days or weeks, or in all history when `within` is absent. */
export type EventCount = { times: number; within?: RelativeTime };
export type SegmentConditionValue =
  | RelativeTime
  | NumberRange
  | EventCount
  | string
  | number
  | boolean
  | string[]
  | number[];

export type SegmentCondition = {
  kind: 'condition';
  fieldKey: string;
  operator: string;
  /** Absent for an operator that takes none. */
  value?: SegmentConditionValue;
};

/** A reference to another, reusable segment: its members, or with `negate` everyone but them. */
export type SegmentRef = {
  kind: 'segment_ref';
  refSegmentId: string;
  negate: boolean;
};

export type SegmentGroup = {
  kind: 'group';
  combinator: SegmentCombinator;
  /** NOT (the group): an exclusion. A person with no value for a field the group tests stays in the segment. */
  negate: boolean;
  children: SegmentNode[];
};

/** A node of a kind this Studio does not know. Kept as it came, so saving the tree sends it back unchanged. */
export type SegmentUnknownNode = {
  kind: 'unknown';
  wire: unknown;
};

export type SegmentNode = SegmentGroup | SegmentCondition | SegmentRef | SegmentUnknownNode;

export type SegmentSummary = {
  id: string;
  name: string;
  description: string | null;
  isReusable: boolean;
  status: SegmentStatus | 'unknown';
  source: SegmentSource | 'unknown';
  /** ISO 8601, in UTC. */
  updatedAt: string;
  /**
   * Moved on by every save and archive. An update sends back the version it read, and a stale one is refused (409).
   * Take it from `getSegment` or a write's answer: the list reads a replica and can trail a save.
   */
  version: number | null;
};

export type SegmentDetail = SegmentSummary & {
  /** `null` for a materialized list, which has no tree, and for a tree whose root this Studio cannot read. */
  root: SegmentGroup | null;
};

export type SegmentListParams = {
  /** Absent: every status but archived. */
  status?: SegmentStatus;
  /** Matches inside the name, ignoring case and accents. */
  q?: string;
  cursor?: string | null;
  /** 1 to 100; the server's default is 50. */
  limit?: number;
};

export type SegmentCreate = {
  name: string;
  description?: string | null;
  /** The server's default is true. */
  isReusable?: boolean;
  status: Exclude<SegmentStatus, 'archived'>;
  root: SegmentGroup;
};

/** A partial update: every field left out keeps what is stored. */
export type SegmentUpdate = {
  expectedVersion: number | null;
  name?: string;
  description?: string | null;
  isReusable?: boolean;
  status?: SegmentStatus;
  root?: SegmentGroup;
};

export const SEGMENT_PREVIEW_STATES = ['running', 'counted', 'failed'] as const;
export type SegmentPreviewState = (typeof SEGMENT_PREVIEW_STATES)[number];

/**
 * Why a preview has no size: the count failed, was cancelled, ran past its time limit, read more data than one count
 * may, or was taken while the warehouse was reloading, when any size would be a wrong zero.
 */
export const SEGMENT_PREVIEW_FAILURES = [
  'query_failed',
  'query_cancelled',
  'timed_out',
  'too_large',
  'source_not_ready',
] as const;
export type SegmentPreviewFailure = (typeof SEGMENT_PREVIEW_FAILURES)[number];

/** The size of a segment not yet saved, as the builder polls it. */
export type SegmentPreview = {
  id: string;
  state: SegmentPreviewState | 'unknown';
  /** Once counted. */
  count: number | null;
  /** When the size was read, ISO 8601 in UTC; once counted. */
  countedAt: string | null;
  /**
   * True when the size was counted earlier, for the same tree over the same data, rather than by a query this request
   * started. False from a server that does not say.
   */
  reused: boolean;
  /** Once failed. */
  failure: SegmentPreviewFailure | 'unknown' | null;
};

/** How far a segment's count can be trusted; `archived` is no longer refreshed, and `refresh_failed` is the last refresh failing, with the reason beside it. */
export const SEGMENT_COUNT_STATES = ['counted', 'stale', 'counting', 'draft', 'archived', 'refresh_failed'] as const;
export type SegmentCountState = (typeof SEGMENT_COUNT_STATES)[number];

/**
 * Why a segment's last refresh failed, in the order the server lists them. server_pray pins its list to this one in a
 * spec, so a new code needs its count line (segmentCountCopy.ts) and an entry here before the server may send it.
 */
export const SEGMENT_REFRESH_ERROR_CODES = [
  'compile_failed',
  'sql_too_large',
  'athena_failed',
  'athena_timeout',
  'empty_result',
  'shrink_refused',
  'growth_refused',
  'store_failed',
  'superseded',
  'cycle',
  'ref_unavailable',
  'list_empty',
  'leaf_failed',
  'source_held',
  'source_unhealthy',
  'source_denied',
  'source_unavailable',
] as const;
export type SegmentRefreshErrorCode = (typeof SEGMENT_REFRESH_ERROR_CODES)[number];

/** A saved segment's size as the last refresh left it. `count` and `countedAt` are null together, never 0 for "not yet". */
export type SegmentCount = {
  state: SegmentCountState | 'unknown';
  count: number | null;
  /** When the size was counted, ISO 8601 in UTC. */
  countedAt: string | null;
  /** Only for `refresh_failed`. `since` is when the first failed run started, ISO 8601 in UTC. */
  failure: { errorCode: SegmentRefreshErrorCode | 'unknown'; since: string } | null;
};
